import type { FeatureFlagValue } from '@posthog/core';
import { PostHog } from 'posthog-js';
import { createContext, useContext } from 'react';

enum PostHogFeatureFlagMap {
  EnableAdditionalExpressOnboardingOptions = 'additional-express-onboarding-options-enabled',
  EnableAiWorkspaceCustomization = 'ai-workspace-customization',
  EnableAlphaApps = 'enable-alpha-apps',
  EnableAppBuilder = 'enable_app_builder',
  EnableAppBuilderInspector = 'app_builder_inspector',
  EnableAppBuilderStopButton = 'app-builder-stop-button',
  EnableAppBuilderStopDuringBuild = 'app-builder-stop-during-build',
  EnableAppBuilderRetryButton = 'app-builder-retry-button',
  EnableAppBuilderTransfer = 'enable_app_builder_transfer',
  EnableAppSetupImprovements = 'appSetupImprovements',
  EnableBetaApps = 'enable-beta-apps',
  EnableCompanyImport = 'enable_company_import',
  EnableCompanyResetPasswords = 'enable_company_reset_passwords',
  EnableContextualActions = 'enable-contextual-actions',
  EnableContractsCheckbox = 'contracts-checkbox',
  EnableContractTemplatesPerf = 'contract-templates-perf',
  EnableIUAccessControl = 'iu_access_control',
  EnableMicrodepositVerification = 'enable-microdeposit-verification',
  EnableMessageAppNotification = 'message-app-notification',
  EnableDuplicateFileName = 'duplicate_file_name',
  EnableFileBatchActions = 'file_batch_actions',
  EnableFilesAppPerformance = 'files-app-performance',
  EnableNewSidebar = 'new-sidebar',
  EnableAuditLogs = 'audit_logs',
  EnableAuditLogPlatform = 'audit_logs_platform',
  EnableFeatureAppVisibility = 'app_visibility_ocmc_support',
  EnableEventsApiDateFilters = 'events_api_date_filters',
  EnableInvoiceRefunds = 'enable_invoice_refunds',
  EnableClientNotificationsMute = 'enable_client_notifications_mute',
  EnableMcpDashboardUi = 'mcp_dashboard_ui',
  EnableMarketplaceAppNotificationSettings = 'enable_marketplace_app_notification_settings',
  EnableIterateOnProjectGithubActions = 'iterate_on_project_github_actions',
  EnableProductAgent = 'enable_product_agent',
  EnableMultiInstanceApps = 'enable_multi_instance_apps',
  EnableStudioConnectors = 'enable_app_integrations',
  EnableStudioSidebar = 'studio-sidebar',
  EnableStudioOnboarding = 'studioOnboarding',
  EnableStudioFTUX = 'studioFTUX',
  EnableClientInvoiceDetails = 'client-invoice-details',
  EnableClientPortalPreviewImprovements = 'client-portal-preview-improvements',
  EnableUpdateAppBuilderHeader = 'update-app-builder-header',
  EnableStudioPricing = 'studio-pricing',
}

export type FlagContextType = Record<
  'EnableManyCompanies' | keyof typeof PostHogFeatureFlagMap,
  boolean
>;

export const DefaultFlagsContext: FlagContextType = {
  // Permanently enabled
  EnableManyCompanies: true,

  // PostHog flags
  EnableAdditionalExpressOnboardingOptions: false,
  EnableAiWorkspaceCustomization: false,
  EnableAlphaApps: false,
  EnableAppBuilder: false,
  EnableAppBuilderInspector: false,
  EnableAppBuilderStopButton: false,
  EnableAppBuilderStopDuringBuild: false,
  EnableAppBuilderRetryButton: false,
  EnableAppBuilderTransfer: false,
  EnableAppSetupImprovements: false,
  EnableBetaApps: false,
  EnableCompanyImport: false,
  EnableCompanyResetPasswords: false,
  EnableContextualActions: false,
  EnableContractsCheckbox: false,
  EnableContractTemplatesPerf: false,
  EnableDuplicateFileName: false,
  EnableFileBatchActions: false,
  EnableFilesAppPerformance: false,
  EnableIUAccessControl: false,
  EnableMessageAppNotification: false,
  EnableMicrodepositVerification: false,
  EnableNewSidebar: false,
  EnableInvoiceRefunds: false,
  EnableClientInvoiceDetails: false,
  EnableClientNotificationsMute: false,
  EnableAuditLogs: false,
  EnableEventsApiDateFilters: false,
  EnableFeatureAppVisibility: false,
  EnableMcpDashboardUi: false,
  EnableMarketplaceAppNotificationSettings: false,
  EnableIterateOnProjectGithubActions: false,
  EnableProductAgent: false,
  EnableAuditLogPlatform: false,
  EnableMultiInstanceApps: false,
  EnableStudioConnectors: false,
  EnableStudioSidebar: false,
  EnableStudioOnboarding: false,
  EnableStudioFTUX: false,
  EnableClientPortalPreviewImprovements: false,
  EnableUpdateAppBuilderHeader: false,
  EnableStudioPricing: false,
};

/**
 * These flags are available on the server side, but are not used so we
 * purposefully exclude them
 */
type ClientOnlyFeatureFlagKeysType = never;

export const getServerFlagsContextValue = (
  posthogFlags: Record<PostHogFeatureFlagMap, FeatureFlagValue>,
): Record<
  Exclude<keyof FlagContextType, ClientOnlyFeatureFlagKeysType>,
  boolean
> => ({
  EnableManyCompanies: true,

  // PostHog flags
  EnableAdditionalExpressOnboardingOptions: Boolean(
    posthogFlags[
      PostHogFeatureFlagMap.EnableAdditionalExpressOnboardingOptions
    ],
  ),
  EnableAiWorkspaceCustomization: Boolean(
    posthogFlags[PostHogFeatureFlagMap.EnableAiWorkspaceCustomization],
  ),
  EnableAlphaApps: Boolean(posthogFlags[PostHogFeatureFlagMap.EnableAlphaApps]),
  EnableAppBuilder: Boolean(
    posthogFlags[PostHogFeatureFlagMap.EnableAppBuilder],
  ),
  EnableAppBuilderInspector: Boolean(
    posthogFlags[PostHogFeatureFlagMap.EnableAppBuilderInspector],
  ),
  EnableAppBuilderStopButton: Boolean(
    posthogFlags[PostHogFeatureFlagMap.EnableAppBuilderStopButton],
  ),
  EnableAppBuilderStopDuringBuild: Boolean(
    posthogFlags[PostHogFeatureFlagMap.EnableAppBuilderStopDuringBuild],
  ),
  EnableAppBuilderRetryButton: Boolean(
    posthogFlags[PostHogFeatureFlagMap.EnableAppBuilderRetryButton],
  ),
  EnableAppBuilderTransfer: Boolean(
    posthogFlags[PostHogFeatureFlagMap.EnableAppBuilderTransfer],
  ),
  EnableAppSetupImprovements: Boolean(
    posthogFlags[PostHogFeatureFlagMap.EnableAppSetupImprovements],
  ),
  EnableBetaApps: Boolean(posthogFlags[PostHogFeatureFlagMap.EnableBetaApps]),
  EnableCompanyImport: Boolean(
    posthogFlags[PostHogFeatureFlagMap.EnableCompanyImport],
  ),
  EnableCompanyResetPasswords: Boolean(
    posthogFlags[PostHogFeatureFlagMap.EnableCompanyResetPasswords],
  ),
  EnableContextualActions: Boolean(
    posthogFlags[PostHogFeatureFlagMap.EnableContextualActions],
  ),
  EnableContractsCheckbox: Boolean(
    posthogFlags[PostHogFeatureFlagMap.EnableContractsCheckbox],
  ),
  EnableContractTemplatesPerf: Boolean(
    posthogFlags[PostHogFeatureFlagMap.EnableContractTemplatesPerf],
  ),
  EnableIUAccessControl: Boolean(
    posthogFlags[PostHogFeatureFlagMap.EnableIUAccessControl],
  ),
  EnableMicrodepositVerification: Boolean(
    posthogFlags[PostHogFeatureFlagMap.EnableMicrodepositVerification],
  ),
  EnableMessageAppNotification: Boolean(
    posthogFlags[PostHogFeatureFlagMap.EnableMessageAppNotification],
  ),
  EnableDuplicateFileName: Boolean(
    posthogFlags[PostHogFeatureFlagMap.EnableDuplicateFileName],
  ),
  EnableFileBatchActions: Boolean(
    posthogFlags[PostHogFeatureFlagMap.EnableFileBatchActions],
  ),
  EnableFilesAppPerformance: Boolean(
    posthogFlags[PostHogFeatureFlagMap.EnableFilesAppPerformance],
  ),
  EnableNewSidebar: Boolean(
    posthogFlags[PostHogFeatureFlagMap.EnableNewSidebar],
  ),
  EnableInvoiceRefunds: Boolean(
    posthogFlags[PostHogFeatureFlagMap.EnableInvoiceRefunds],
  ),
  EnableClientInvoiceDetails: Boolean(
    posthogFlags[PostHogFeatureFlagMap.EnableClientInvoiceDetails],
  ),
  EnableClientNotificationsMute: Boolean(
    posthogFlags[PostHogFeatureFlagMap.EnableClientNotificationsMute],
  ),
  EnableAuditLogs: Boolean(posthogFlags[PostHogFeatureFlagMap.EnableAuditLogs]),
  EnableFeatureAppVisibility: Boolean(
    posthogFlags[PostHogFeatureFlagMap.EnableFeatureAppVisibility],
  ),
  EnableEventsApiDateFilters: Boolean(
    posthogFlags[PostHogFeatureFlagMap.EnableEventsApiDateFilters],
  ),
  EnableMcpDashboardUi: Boolean(
    posthogFlags[PostHogFeatureFlagMap.EnableMcpDashboardUi],
  ),
  EnableMarketplaceAppNotificationSettings: Boolean(
    posthogFlags[
      PostHogFeatureFlagMap.EnableMarketplaceAppNotificationSettings
    ],
  ),
  EnableIterateOnProjectGithubActions: Boolean(
    posthogFlags[PostHogFeatureFlagMap.EnableIterateOnProjectGithubActions],
  ),
  EnableProductAgent: Boolean(
    posthogFlags[PostHogFeatureFlagMap.EnableProductAgent],
  ),
  EnableAuditLogPlatform: Boolean(
    posthogFlags[PostHogFeatureFlagMap.EnableAuditLogPlatform],
  ),
  EnableMultiInstanceApps: Boolean(
    posthogFlags[PostHogFeatureFlagMap.EnableMultiInstanceApps],
  ),
  EnableStudioConnectors: Boolean(
    posthogFlags[PostHogFeatureFlagMap.EnableStudioConnectors],
  ),
  EnableStudioSidebar: Boolean(
    posthogFlags[PostHogFeatureFlagMap.EnableStudioSidebar],
  ),
  EnableStudioOnboarding: Boolean(
    posthogFlags[PostHogFeatureFlagMap.EnableStudioOnboarding],
  ),
  EnableStudioFTUX: Boolean(
    posthogFlags[PostHogFeatureFlagMap.EnableStudioFTUX],
  ),
  EnableClientPortalPreviewImprovements: Boolean(
    posthogFlags[PostHogFeatureFlagMap.EnableClientPortalPreviewImprovements],
  ),
  EnableUpdateAppBuilderHeader: Boolean(
    posthogFlags[PostHogFeatureFlagMap.EnableUpdateAppBuilderHeader],
  ),
  EnableStudioPricing: Boolean(
    posthogFlags[PostHogFeatureFlagMap.EnableStudioPricing],
  ),
});

export const getClientFlagsContextValue = (
  posthogFlags: PostHog,
): Record<keyof FlagContextType, boolean | undefined> => ({
  EnableManyCompanies: true,

  // PostHog flags
  EnableAdditionalExpressOnboardingOptions: posthogFlags.isFeatureEnabled(
    PostHogFeatureFlagMap.EnableAdditionalExpressOnboardingOptions,
  ),
  EnableAiWorkspaceCustomization: posthogFlags.isFeatureEnabled(
    PostHogFeatureFlagMap.EnableAiWorkspaceCustomization,
  ),
  EnableAlphaApps: posthogFlags.isFeatureEnabled(
    PostHogFeatureFlagMap.EnableAlphaApps,
  ),
  EnableAppBuilder: posthogFlags.isFeatureEnabled(
    PostHogFeatureFlagMap.EnableAppBuilder,
  ),
  EnableAppBuilderInspector: posthogFlags.isFeatureEnabled(
    PostHogFeatureFlagMap.EnableAppBuilderInspector,
  ),
  EnableAppBuilderStopButton: posthogFlags.isFeatureEnabled(
    PostHogFeatureFlagMap.EnableAppBuilderStopButton,
  ),
  EnableAppBuilderStopDuringBuild: posthogFlags.isFeatureEnabled(
    PostHogFeatureFlagMap.EnableAppBuilderStopDuringBuild,
  ),
  EnableAppBuilderRetryButton: posthogFlags.isFeatureEnabled(
    PostHogFeatureFlagMap.EnableAppBuilderRetryButton,
  ),
  EnableAppBuilderTransfer: posthogFlags.isFeatureEnabled(
    PostHogFeatureFlagMap.EnableAppBuilderTransfer,
  ),
  EnableAppSetupImprovements: posthogFlags.isFeatureEnabled(
    PostHogFeatureFlagMap.EnableAppSetupImprovements,
  ),
  EnableBetaApps: posthogFlags.isFeatureEnabled(
    PostHogFeatureFlagMap.EnableBetaApps,
  ),
  EnableCompanyImport: posthogFlags.isFeatureEnabled(
    PostHogFeatureFlagMap.EnableCompanyImport,
  ),
  EnableCompanyResetPasswords: posthogFlags.isFeatureEnabled(
    PostHogFeatureFlagMap.EnableCompanyResetPasswords,
  ),
  EnableContextualActions: posthogFlags.isFeatureEnabled(
    PostHogFeatureFlagMap.EnableContextualActions,
  ),
  EnableContractsCheckbox: posthogFlags.isFeatureEnabled(
    PostHogFeatureFlagMap.EnableContractsCheckbox,
  ),
  EnableContractTemplatesPerf: posthogFlags.isFeatureEnabled(
    PostHogFeatureFlagMap.EnableContractTemplatesPerf,
  ),
  EnableIUAccessControl: posthogFlags.isFeatureEnabled(
    PostHogFeatureFlagMap.EnableIUAccessControl,
  ),
  EnableMicrodepositVerification: posthogFlags.isFeatureEnabled(
    PostHogFeatureFlagMap.EnableMicrodepositVerification,
  ),
  EnableMessageAppNotification: posthogFlags.isFeatureEnabled(
    PostHogFeatureFlagMap.EnableMessageAppNotification,
  ),
  EnableDuplicateFileName: posthogFlags.isFeatureEnabled(
    PostHogFeatureFlagMap.EnableDuplicateFileName,
  ),
  EnableFileBatchActions: posthogFlags.isFeatureEnabled(
    PostHogFeatureFlagMap.EnableFileBatchActions,
  ),
  EnableFilesAppPerformance: posthogFlags.isFeatureEnabled(
    PostHogFeatureFlagMap.EnableFilesAppPerformance,
  ),
  EnableNewSidebar: posthogFlags.isFeatureEnabled(
    PostHogFeatureFlagMap.EnableNewSidebar,
  ),
  EnableInvoiceRefunds: posthogFlags.isFeatureEnabled(
    PostHogFeatureFlagMap.EnableInvoiceRefunds,
  ),
  EnableClientInvoiceDetails: posthogFlags.isFeatureEnabled(
    PostHogFeatureFlagMap.EnableClientInvoiceDetails,
  ),
  EnableClientNotificationsMute: posthogFlags.isFeatureEnabled(
    PostHogFeatureFlagMap.EnableClientNotificationsMute,
  ),
  EnableAuditLogs: posthogFlags.isFeatureEnabled(
    PostHogFeatureFlagMap.EnableAuditLogs,
  ),
  EnableEventsApiDateFilters: posthogFlags.isFeatureEnabled(
    PostHogFeatureFlagMap.EnableEventsApiDateFilters,
  ),
  EnableFeatureAppVisibility: posthogFlags.isFeatureEnabled(
    PostHogFeatureFlagMap.EnableFeatureAppVisibility,
  ),
  EnableMcpDashboardUi: posthogFlags.isFeatureEnabled(
    PostHogFeatureFlagMap.EnableMcpDashboardUi,
  ),
  EnableMarketplaceAppNotificationSettings: posthogFlags.isFeatureEnabled(
    PostHogFeatureFlagMap.EnableMarketplaceAppNotificationSettings,
  ),
  EnableIterateOnProjectGithubActions: posthogFlags.isFeatureEnabled(
    PostHogFeatureFlagMap.EnableIterateOnProjectGithubActions,
  ),
  EnableProductAgent: posthogFlags.isFeatureEnabled(
    PostHogFeatureFlagMap.EnableProductAgent,
  ),
  EnableAuditLogPlatform: posthogFlags.isFeatureEnabled(
    PostHogFeatureFlagMap.EnableAuditLogPlatform,
  ),
  EnableMultiInstanceApps: posthogFlags.isFeatureEnabled(
    PostHogFeatureFlagMap.EnableMultiInstanceApps,
  ),
  EnableStudioConnectors: posthogFlags.isFeatureEnabled(
    PostHogFeatureFlagMap.EnableStudioConnectors,
  ),
  EnableStudioSidebar: posthogFlags.isFeatureEnabled(
    PostHogFeatureFlagMap.EnableStudioSidebar,
  ),
  EnableStudioOnboarding: posthogFlags.isFeatureEnabled(
    PostHogFeatureFlagMap.EnableStudioOnboarding,
  ),
  EnableStudioFTUX: posthogFlags.isFeatureEnabled(
    PostHogFeatureFlagMap.EnableStudioFTUX,
  ),
  EnableClientPortalPreviewImprovements: posthogFlags.isFeatureEnabled(
    PostHogFeatureFlagMap.EnableClientPortalPreviewImprovements,
  ),
  EnableUpdateAppBuilderHeader: posthogFlags.isFeatureEnabled(
    PostHogFeatureFlagMap.EnableUpdateAppBuilderHeader,
  ),
  EnableStudioPricing: posthogFlags.isFeatureEnabled(
    PostHogFeatureFlagMap.EnableStudioPricing,
  ),
});

export const FlagsContext = createContext(DefaultFlagsContext);

/**
 * Custom hook to access feature flags from the context. This abstracts away the
 * underlying implementation and allows components to easily check if a feature is enabled.
 */
export function useFeatureFlags(): FlagContextType {
  return useContext(FlagsContext);
}
